import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, TrendingUp, Clock, ArrowUpRight } from 'lucide-react';
import { repository, POPULAR_SEARCHES } from '../lib/repository';
import { formatPrice } from '../lib/utils';

const RECENT_KEY = 'evermark-recent-searches-v1';
const RECENT_MAX = 6;

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
  });
  const inputRef = useRef(null);
  const openerRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      openerRef.current = document.activeElement;
      setQuery('');
      setActiveIdx(-1);
      setTimeout(() => inputRef.current?.focus(), 60);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      openerRef.current?.focus?.();
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Focus trap while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const container = document.querySelector('[data-testid="search-overlay"]');
      if (!container) return;
      const focusables = container.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const commitRecent = useCallback((term) => {
    const t = term.trim();
    if (!t) return;
    setRecent((prev) => {
      const next = [t, ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearRecent = () => { setRecent([]); localStorage.removeItem(RECENT_KEY); };

  const { products, categories, suggestions } = useMemo(
    () => repository.search(query, 6),
    [query]
  );

  // Keyboard navigation across suggestions
  const totalSuggestions = suggestions.length;
  useEffect(() => {
    const onKey = (e) => {
      if (!open) return;
      if (e.key === 'Escape') { onClose(); return; }
      if (!query) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(totalSuggestions - 1, i + 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(-1, i - 1)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const term = activeIdx >= 0 ? suggestions[activeIdx] : query;
        commitRecent(term);
        nav(`/shop?q=${encodeURIComponent(term)}`);
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, query, activeIdx, totalSuggestions, suggestions, commitRecent, nav, onClose]);

  const applyTerm = (term) => {
    setQuery(term);
    setActiveIdx(-1);
    inputRef.current?.focus();
  };

  const submitTerm = (term) => {
    commitRecent(term);
    nav(`/shop?q=${encodeURIComponent(term)}`);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="search-overlay"
          className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-2xl overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.85, 0, 0.15, 1] }}
          role="dialog"
          aria-modal="true"
          aria-label="Search"
          data-testid="search-overlay"
        >
          <button
            type="button"
            aria-label="Close search"
            data-testid="search-close"
            onClick={onClose}
            className="absolute top-6 right-6 h-10 w-10 inline-flex items-center justify-center hover:bg-foreground/5 z-10"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="container pt-24 md:pt-32 pb-32">
            <p className="overline text-muted-foreground">Search the catalog</p>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.85, 0, 0.15, 1] }}
              className="mt-6 flex items-center gap-4 border-b border-foreground pb-4"
            >
              <Search className="h-5 w-5 opacity-60" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActiveIdx(-1); }}
                type="text"
                data-testid="search-input"
                placeholder="Merino, coat, linen, cashmere…"
                className="flex-1 bg-transparent outline-none font-display text-3xl md:text-5xl placeholder:text-muted-foreground/50"
                autoComplete="off"
              />
              {query && (
                <button onClick={() => setQuery('')} aria-label="Clear" data-testid="search-clear" className="opacity-60 hover:opacity-100">
                  <X className="h-5 w-5" />
                </button>
              )}
            </motion.div>

            <div className="mt-10 grid gap-14">
              {/* Idle state: recent + popular */}
              {!query && (
                <>
                  {recent.length > 0 && (
                    <section data-testid="search-recent">
                      <div className="mb-4 flex items-baseline justify-between">
                        <p className="overline text-muted-foreground inline-flex items-center gap-2">
                          <Clock className="h-3 w-3" /> Recent Searches
                        </p>
                        <button onClick={clearRecent} className="overline text-muted-foreground link-underline" data-testid="search-recent-clear">Clear</button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {recent.map((r) => (
                          <button
                            key={r}
                            onClick={() => applyTerm(r)}
                            data-testid={`search-recent-${r.toLowerCase()}`}
                            className="px-4 py-2 border border-border text-sm hover:border-foreground transition-colors duration-500"
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </section>
                  )}
                  <section data-testid="search-popular">
                    <p className="overline text-muted-foreground mb-4 inline-flex items-center gap-2">
                      <TrendingUp className="h-3 w-3" /> Popular Right Now
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_SEARCHES.map((s) => (
                        <button
                          key={s}
                          onClick={() => applyTerm(s)}
                          data-testid={`search-popular-${s.toLowerCase()}`}
                          className="px-4 py-2 border border-border text-sm hover:border-foreground transition-colors duration-500"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </section>
                </>
              )}

              {/* Active query state */}
              {query && (
                <div className="grid gap-10">
                  {/* Autocomplete suggestions */}
                  {suggestions.length > 0 && (
                    <section>
                      <p className="overline text-muted-foreground mb-3">Suggestions</p>
                      <ul data-testid="search-suggestions">
                        {suggestions.map((s, i) => (
                          <li key={s}>
                            <button
                              onMouseEnter={() => setActiveIdx(i)}
                              onClick={() => submitTerm(s)}
                              data-testid={`search-suggestion-${i}`}
                              className={`group w-full text-left flex items-center justify-between py-3 border-b border-border transition-colors duration-500 ${activeIdx === i ? 'bg-foreground/5' : ''}`}
                            >
                              <span className="text-lg">
                                {s.split(new RegExp(`(${query})`, 'gi')).map((part, idx) =>
                                  part.toLowerCase() === query.toLowerCase()
                                    ? <mark key={idx} className="bg-transparent text-foreground font-medium">{part}</mark>
                                    : <span key={idx} className="text-muted-foreground">{part}</span>
                                )}
                              </span>
                              <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {/* Category matches */}
                  {categories.length > 0 && (
                    <section>
                      <p className="overline text-muted-foreground mb-3">Categories</p>
                      <div className="flex flex-wrap gap-2" data-testid="search-categories">
                        {categories.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/shop/${c.slug}`}
                            onClick={onClose}
                            className="px-4 py-2 border border-border text-sm hover:border-foreground transition-colors duration-500"
                          >
                            {c.name}
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Products */}
                  {products.length > 0 && (
                    <section>
                      <div className="mb-3 flex items-baseline justify-between">
                        <p className="overline text-muted-foreground">Pieces</p>
                        <button onClick={() => submitTerm(query)} className="overline link-underline" data-testid="search-view-all">
                          View all matches
                        </button>
                      </div>
                      <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8" data-testid="search-results">
                        {products.map((p) => (
                          <li key={p.id}>
                            <Link to={`/product/${p.slug}`} onClick={onClose} className="group block">
                              <div className="aspect-[3/4] bg-muted overflow-hidden">
                                <img src={p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-cinema group-hover:scale-[1.04]" />
                              </div>
                              <p className="mt-3 text-sm link-underline">{p.name}</p>
                              <p className="overline text-muted-foreground mt-1 tabular">
                                {p.salePrice ? `${formatPrice(p.salePrice)} · ${formatPrice(p.price)}` : formatPrice(p.price)}
                              </p>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {products.length === 0 && categories.length === 0 && suggestions.length === 0 && (
                    <p className="text-muted-foreground py-8" data-testid="search-empty">Nothing matches "{query}" — try another word.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
